import { useEffect, useMemo, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, FileSpreadsheet, Calendar, Receipt } from "lucide-react";

const todayISO = () => new Date().toISOString().slice(0, 10);
const startOfMonth = (iso) => { const d = new Date(iso); d.setDate(1); return d.toISOString().slice(0, 10); };
const endOfMonth   = (iso) => { const d = new Date(iso); d.setMonth(d.getMonth() + 1); d.setDate(0); return d.toISOString().slice(0, 10); };
const subMonths    = (iso, n) => { const d = new Date(iso); d.setMonth(d.getMonth() - n); return d.toISOString().slice(0, 10); };

export default function ReportSubcontractorInvoices() {
  const [poRange, setPoRange] = useState("last_month");
  const [poFrom, setPoFrom]   = useState(startOfMonth(todayISO()));
  const [poTo, setPoTo]       = useState(todayISO());
  const [contactId, setContactId] = useState("all");
  const [subs, setSubs] = useState([]);

  useEffect(() => {
    api.get("/contacts").then(r => {
      const list = (r.data || []).filter(c => c.type === "customer");
      list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setSubs(list);
    }).catch(() => setSubs([]));
  }, []);

  const poRangeResolved = useMemo(() => {
    const t = todayISO();
    if (poRange === "this_week")  { const d = new Date(t); const dow = (d.getDay() + 6) % 7; d.setDate(d.getDate() - dow); const m = d.toISOString().slice(0, 10); const e = new Date(d); e.setDate(e.getDate() + 6); return { from: m, to: e.toISOString().slice(0, 10) }; }
    if (poRange === "last_week")  { const d = new Date(t); const dow = (d.getDay() + 6) % 7; d.setDate(d.getDate() - dow - 7); const m = d.toISOString().slice(0, 10); const e = new Date(d); e.setDate(e.getDate() + 6); return { from: m, to: e.toISOString().slice(0, 10) }; }
    if (poRange === "this_month") return { from: startOfMonth(t), to: endOfMonth(t) };
    if (poRange === "last_month") { const lm = subMonths(t, 1); return { from: startOfMonth(lm), to: endOfMonth(lm) }; }
    if (poRange === "last_3m")    return { from: subMonths(t, 3), to: t };
    if (poRange === "last_6m")    return { from: subMonths(t, 6), to: t };
    if (poRange === "last_12m")   return { from: subMonths(t, 12), to: t };
    if (poRange === "ytd")        { const d = new Date(); return { from: `${d.getFullYear()}-01-01`, to: t }; }
    if (poRange === "last_year")  { const y = new Date().getFullYear() - 1; return { from: `${y}-01-01`, to: `${y}-12-31` }; }
    return { from: poFrom, to: poTo };
  }, [poRange, poFrom, poTo]);

  const poQ = `?date_from=${poRangeResolved.from}&date_to=${poRangeResolved.to}${contactId !== "all" ? `&contact_id=${encodeURIComponent(contactId)}` : ""}`;

  return (
    <div data-testid="report-subcontractor-invoices-page" className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Reporting · Subcontractors</div>
        <h1 className="font-display text-3xl tracking-tight font-semibold text-slate-900 mt-2">Subcontractor invoice reports</h1>
        <p className="text-sm text-slate-600 mt-1">How many POs were issued in the period, with subtotal, VAT and grand total — and which are paid, invoiced or outstanding.</p>
      </div>

      <Card data-testid="po-summary-card" className="rounded-sm border border-slate-200 shadow-none p-6 space-y-4">
        <div>
          <Label className="text-xs text-slate-600">Subcontractor</Label>
          <Select value={contactId} onValueChange={setContactId}>
            <SelectTrigger data-testid="po-contact-filter" className="mt-1 rounded-sm border-slate-300 max-w-md"><SelectValue placeholder="All subcontractors" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All subcontractors</SelectItem>
              {subs.map(s => <SelectItem key={s.id} value={s.id}>{s.name || s.email || s.id}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <Label className="text-xs text-slate-600">Period</Label>
            <Select value={poRange} onValueChange={setPoRange}>
              <SelectTrigger data-testid="po-range-preset" className="mt-1 rounded-sm border-slate-300"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="this_week">This week</SelectItem>
                <SelectItem value="last_week">Last week</SelectItem>
                <SelectItem value="this_month">This month</SelectItem>
                <SelectItem value="last_month">Last month</SelectItem>
                <SelectItem value="last_3m">Last 3 months</SelectItem>
                <SelectItem value="last_6m">Last 6 months</SelectItem>
                <SelectItem value="last_12m">Last 12 months</SelectItem>
                <SelectItem value="ytd">Year to date</SelectItem>
                <SelectItem value="last_year">Last year</SelectItem>
                <SelectItem value="custom">Custom range</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {poRange === "custom" ? (
            <>
              <div>
                <Label className="text-xs text-slate-600">From</Label>
                <Input data-testid="po-from" type="date" value={poFrom} onChange={(e) => setPoFrom(e.target.value)} className="mt-1 rounded-sm border-slate-300" />
              </div>
              <div>
                <Label className="text-xs text-slate-600">To</Label>
                <Input data-testid="po-to" type="date" value={poTo} onChange={(e) => setPoTo(e.target.value)} className="mt-1 rounded-sm border-slate-300" />
              </div>
            </>
          ) : (
            <div className="md:col-span-2 text-xs text-slate-500 inline-flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5" /> Covering: <span className="font-medium tabular-nums">{poRangeResolved.from} → {poRangeResolved.to}</span>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <Button asChild variant="outline" data-testid="po-summary-pdf" className="rounded-sm border-slate-300">
              <a href={fileUrl(`/reports/subcontractor-invoices-summary.pdf${poQ}`)} download rel="noopener">
                <Download className="w-4 h-4 mr-1.5" /> PDF
              </a>
            </Button>
            <Button asChild data-testid="po-summary-xlsx" className="rounded-sm bg-slate-900 hover:bg-slate-800">
              <a href={fileUrl(`/reports/subcontractor-invoices-summary.xlsx${poQ}`)} download rel="noopener">
                <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Excel
              </a>
            </Button>
          </div>
        </div>
        <div className="mt-4 pt-3 border-t border-slate-200 text-xs text-slate-500 inline-flex items-center gap-2">
          <Receipt className="w-3.5 h-3.5" /> Excel exports include 2 sheets: <span className="font-medium">Summary (counts + by month)</span> and <span className="font-medium">All POs</span> with subtotal / VAT / total per PO.
        </div>
      </Card>
    </div>
  );
}
